/**
 * Générateur — La probabilité est un nombre compris entre 0 et 1.
 * Attendu ID : 169 | Niveau : 4ème | Domaine : Probabilités
 *
 * PLAFOND (attendus de fin de 4e, Eduscol, `.claude/reference/eduscol-attendus-fin-4e.md`) :
 *   l. 216 « Il sait que la probabilité d'un événement est un nombre compris
 *          entre 0 et 1. » (aucun exemple de réussite propre).
 * Cours du prof (19Proba, p. 2) : « La probabilité d'un évènement est un nombre
 * compris entre 0 et 1 » ; p = 0 pour un événement impossible, p = 1 pour un
 * événement certain.
 *
 * Revue 2026-09-25 (vague « sept objectifs 4e fermés », DECISIONS_167-170.md) :
 * attendu de pur rappel (entretien de 6e, BO l. 1152-1154) : deux QCM, rien
 * d'autre ; ni rappel brouillon ni calculatrice ; la règle n'est plus écrite
 * dans l'énoncé (elle soufflait la réponse, 100 % des formats de juillet).
 *   1. « Est-ce une probabilité ? » (id `nombre`, `nombre_qcm_unique`) :
 *      « Lequel de ces nombres peut être une probabilité ? » ou « … ne peut
 *      pas être … ? » (variante tirée, _meta.params.variante) ; décimal,
 *      fraction (KaTeX), pourcentage mêlés ; 0, 1, 0 % et 100 % comptent parmi
 *      les VALIDES. « Ne peut pas » : trois valides et un intrus (la clé),
 *      tiré parmi superieur_a_1 (principale, deux tirages sur trois) et
 *      negatif. « Peut » : un valide (la clé) et trois intrus de familles
 *      distinctes (superieur_a_1 en décimal, superieur_a_1 en fraction ou en
 *      pourcentage, negatif). Dans les deux variantes, la clé n'est ni la
 *      seule de sa forme ni la plus longue (vérifié, throw sinon).
 *   2. « Ce que dit le nombre » (id `bornes`, `bornes_qcm_unique`) : « La
 *      probabilité d'un événement est 0,02. Cet événement est-il impossible,
 *      certain, ou ni l'un ni l'autre ? » ; trois réponses fixes ; la nature
 *      visée tirée d'abord (un tiers chacune) ; pièges certain_pour_tres_probable
 *      (0,98 dit certain), impossible_pour_rare (0,02 dit impossible),
 *      bornes_exclues (0 ou 1 dit « ni l'un ni l'autre »).
 *
 * SORTI (et pourquoi) : la carte « Sur l'échelle » et ses quatre formats
 * (automatisme 5e 1081 / 1083 ; « 1/2 » comme libellé pour 0,45 ; relier sans
 * leurre ; classer à écritures de même valeur ; consigne fausse du drag) ; le
 * Vrai/Faux (il redit le QCM) ; les deux QCM « peut » / « ne peut pas » comme
 * onglets séparés (une seule clé, la variante est tirée) ; la phrase-règle des
 * énoncés ; les fractions en texte brut.
 * SACRIFIÉS : rien (le « 150 % » de la fiche 5e 1082 est un intrus admis).
 * Second chemin : chaque option est RELUE en nombre (décimal, fraction,
 * pourcentage) et jugée dans [0 ; 1] ; une seule réponse possible, throw sinon.
 * Solveur test-side : tests/solvers/probabilites-4e.js ; auto-test :
 * tests/gen169.self.test.js.
 */
import { randomChoice, randInt, shuffle, makeQcm, fracTex } from '../../../utils';

const ATTENDU_ID = 169;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_NOMBRE = 'Est-ce une probabilité ?';
const L_BORNES = 'Ce que dit le nombre';

// ─── Les nombres : { texte affiché, valeur, forme, code } ─────

const dec = (s, code) => ({ texte: s, valeur: Number(s.replace('−', '-').replace(',', '.')), forme: 'decimal', code, longueur: s.length });
const pct = (n, code) => ({ texte: `${n} %`, valeur: n / 100, forme: 'pourcentage', code, longueur: String(n).length + 2 });
const frac = (n, d, code) => ({ texte: fracTex(n, d), valeur: n / d, forme: 'fraction', code, longueur: `${n}/${d}`.length });

/** Les valides : bornes (0, 1, 0 %, 100 %) et nombres ordinaires de [0 ; 1], sous trois écritures. */
const VALIDES = [
    dec('0', 'valide_borne'), dec('1', 'valide_borne'), pct(0, 'valide_borne'), pct(100, 'valide_borne'),
    dec('0,5', 'valide'), dec('0,25', 'valide'), dec('0,75', 'valide'), dec('0,9', 'valide'), dec('0,1', 'valide'),
    dec('0,05', 'valide'), dec('0,3', 'valide'), dec('0,62', 'valide'), dec('0,08', 'valide'), dec('0,45', 'valide'),
    dec('0,125', 'valide'), dec('0,375', 'valide'), dec('0,875', 'valide'),
    frac(1, 2, 'valide'), frac(3, 4, 'valide'), frac(1, 4, 'valide'), frac(1, 10, 'valide'), frac(1, 6, 'valide'),
    frac(2, 5, 'valide'), frac(5, 8, 'valide'), frac(7, 10, 'valide'), frac(1, 3, 'valide'), frac(9, 10, 'valide'),
    frac(11, 20, 'valide'), frac(13, 25, 'valide'),
    pct(50, 'valide'), pct(40, 'valide'), pct(25, 'valide'), pct(75, 'valide'), pct(8, 'valide'), pct(99, 'valide'), pct(12, 'valide'), pct(60, 'valide'),
];

/** Les intrus au-dessus de 1, par forme. */
const SUP_UN = {
    decimal: [dec('1,3', 'superieur_a_1'), dec('1,5', 'superieur_a_1'), dec('1,2', 'superieur_a_1'), dec('1,05', 'superieur_a_1'), dec('2', 'superieur_a_1'), dec('1,01', 'superieur_a_1'), dec('2,5', 'superieur_a_1'), dec('1,125', 'superieur_a_1'), dec('1,375', 'superieur_a_1')],
    fraction: [frac(5, 4, 'superieur_a_1'), frac(7, 5, 'superieur_a_1'), frac(4, 3, 'superieur_a_1'), frac(3, 2, 'superieur_a_1'), frac(9, 8, 'superieur_a_1'), frac(11, 10, 'superieur_a_1')],
    pourcentage: [pct(150, 'superieur_a_1'), pct(120, 'superieur_a_1'), pct(101, 'superieur_a_1'), pct(200, 'superieur_a_1'), pct(110, 'superieur_a_1')],
};
/** Les intrus négatifs (le signe moins de la typographie, U+2212). */
const NEGATIFS = [
    dec('−0,2', 'negatif'), dec('−0,5', 'negatif'), dec('−1', 'negatif'), dec('−0,1', 'negatif'), dec('−0,75', 'negatif'),
    { ...frac(-1, 2, 'negatif'), longueur: 4 }, { ...frac(-3, 4, 'negatif'), longueur: 4 },
    { ...pct(-20, 'negatif'), longueur: 5 }, { ...pct(-5, 'negatif'), longueur: 4 },
];

const estValide = (n) => n.valeur >= 0 && n.valeur <= 1;

/** Des nombres deux à deux distincts par la valeur (0,5 et 1/2 ne cohabitent pas). */
const valeursDistinctes = (liste) => new Set(liste.map((n) => n.valeur.toFixed(9))).size === liste.length;

/** La clé partage sa forme avec au moins une autre option et n'est pas la plus longue. */
const cleNoyee = (cle, autres) => autres.some((o) => o.forme === cle.forme) && autres.some((o) => o.longueur >= cle.longueur);

// ═════════════════════════════════════════════════════════════
// CARTE « Est-ce une probabilité ? »
// ═════════════════════════════════════════════════════════════

const tirerNePeutPas = () => {
    for (let essai = 0; essai < 60; essai++) {
        const cle = randInt(1, 3) === 1 ? randomChoice(NEGATIFS) : randomChoice(SUP_UN[randomChoice(['decimal', 'fraction', 'pourcentage'])]);
        // Une valide de la forme de la clé d'abord, puis deux autres.
        const memeForme = shuffle(VALIDES.filter((v) => v.forme === cle.forme));
        const autres = shuffle(VALIDES.filter((v) => v !== memeForme[0])).slice(0, 2);
        const valides = [memeForme[0], ...autres];
        if (!valeursDistinctes(valides) || !cleNoyee(cle, valides)) continue;
        return { cle, autres: valides };
    }
    // Repli déterministe vérifié : 1,3 ; 0,3 ; 100 % ; 3/4.
    return { cle: SUP_UN.decimal[0], autres: [dec('0,3', 'valide'), pct(100, 'valide_borne'), frac(3, 4, 'valide')] };
};

const tirerPeut = () => {
    for (let essai = 0; essai < 60; essai++) {
        const cle = randomChoice(VALIDES);
        // Trois intrus de familles distinctes : > 1 en décimal, > 1 en fraction ou
        // en pourcentage (la forme de la clé quand elle n'est pas décimale), négatif.
        const formeBis = cle.forme === 'decimal' ? randomChoice(['fraction', 'pourcentage']) : cle.forme;
        const intrus = [randomChoice(SUP_UN.decimal), randomChoice(SUP_UN[formeBis]), randomChoice(NEGATIFS)];
        if (!valeursDistinctes([cle, ...intrus]) || !cleNoyee(cle, intrus)) continue;
        return { cle, autres: intrus };
    }
    // Repli déterministe vérifié : 0,3 ; 1,3 ; 150 % ; −0,2.
    return { cle: dec('0,3', 'valide'), autres: [SUP_UN.decimal[0], SUP_UN.pourcentage[0], NEGATIFS[0]] };
};

const genNombreQcm = () => {
    const variante = randomChoice(['peut', 'ne_peut_pas']);
    const { cle, autres } = variante === 'peut' ? tirerPeut() : tirerNePeutPas();
    const options = [cle, ...autres];
    // Second chemin : chaque option relue en nombre ; une seule répond à la question.
    const repond = (n) => (variante === 'peut' ? estValide(n) : !estValide(n));
    if (options.filter(repond).length !== 1 || !repond(cle)) throw new Error('gen_169 : plusieurs réponses possibles');
    if (new Set(options.map((o) => o.texte)).size !== 4 || !valeursDistinctes(options) || !cleNoyee(cle, autres)) throw new Error('gen_169 : options');
    const question = variante === 'peut'
        ? 'Lequel de ces nombres peut être une probabilité ?'
        : 'Lequel de ces nombres ne peut pas être une probabilité ?';
    const payload = makeQcm(options.map((o) => o.texte), cle.texte, `<p><b>${question}</b></p>`, opts(L_NOMBRE));
    payload._meta.params = { variante, cle: cle.texte, forme: cle.forme, intrus: variante === 'peut' ? autres.map((o) => o.code) : [cle.code], distracteurs: autres.map((o) => o.code) };
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Ce que dit le nombre »
// ═════════════════════════════════════════════════════════════

const NATURES = { impossible: 'Impossible', certain: 'Certain', ni: "Ni l'un ni l'autre" };

const IMPOSSIBLES = [dec('0', 'bornes_exclues'), pct(0, 'bornes_exclues'), frac(0, 5, 'bornes_exclues')];
const CERTAINS = [dec('1', 'bornes_exclues'), pct(100, 'bornes_exclues'), frac(5, 5, 'bornes_exclues')];
const TRES_PROBABLES = [dec('0,98', 'certain_pour_tres_probable'), dec('0,99', 'certain_pour_tres_probable'), dec('0,95', 'certain_pour_tres_probable'),
    pct(98, 'certain_pour_tres_probable'), pct(99, 'certain_pour_tres_probable'), frac(99, 100, 'certain_pour_tres_probable'), dec('0,999', 'certain_pour_tres_probable')];
const RARES = [dec('0,02', 'impossible_pour_rare'), dec('0,01', 'impossible_pour_rare'), dec('0,05', 'impossible_pour_rare'),
    pct(2, 'impossible_pour_rare'), pct(1, 'impossible_pour_rare'), frac(1, 100, 'impossible_pour_rare'), dec('0,001', 'impossible_pour_rare')];
const ORDINAIRES = [frac(3, 4, 'ordinaire'), dec('0,5', 'ordinaire'), pct(40, 'ordinaire'), frac(1, 3, 'ordinaire'), dec('0,6', 'ordinaire'),
    frac(2, 5, 'ordinaire'), dec('0,25', 'ordinaire'), pct(75, 'ordinaire'), dec('0,9', 'ordinaire'), pct(10, 'ordinaire'), frac(1, 6, 'ordinaire')];

const natureDe = (n) => (n.valeur === 0 ? 'impossible' : n.valeur === 1 ? 'certain' : 'ni');

const genBornesQcm = () => {
    const nature = randomChoice(['impossible', 'certain', 'ni']);
    let n;
    if (nature === 'impossible') n = randomChoice(IMPOSSIBLES);
    else if (nature === 'certain') n = randomChoice(CERTAINS);
    else n = randomChoice(randomChoice([TRES_PROBABLES, RARES, ORDINAIRES]));
    // Second chemin : la nature relue sur la valeur.
    if (natureDe(n) !== nature) throw new Error(`gen_169 : ${n.texte} n'est pas ${nature}`);
    const choix = [NATURES.impossible, NATURES.certain, NATURES.ni];
    const payload = makeQcm(choix, NATURES[nature],
        `<p><b>La probabilité d'un événement est ${n.texte}. Cet événement est-il impossible, certain, ou ni l'un ni l'autre ?</b></p>`,
        opts(L_BORNES));
    payload._meta.params = { nombre: n.texte, forme: n.forme, nature, distracteurs: n.code === 'ordinaire' ? [] : [n.code] };
    return payload;
};

// ═════════ Contrat des distracteurs (données pures, jamais lues à l'exécution) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    nombre_qcm_unique: {
        support: '« Lequel de ces nombres peut / ne peut pas être une probabilité ? » ; décimal, fraction (KaTeX), pourcentage mêlés ; la règle n\'est plus écrite dans l\'énoncé',
        avertissement: 'aucun item DEPP lu sur ce point ; familles de la fiche 5e 1082 (« ne dépasse jamais 1 ni 100 % ») et du cours p. 2',
        familles: {
            superieur_a_1: 'un nombre au-dessus de 1 pris pour une probabilité : 1,3 ; 5/4 (DEPP 5e 2025 q.1, « une fraction est ≤ 1 ») ; 150 % (la principale, deux tirages sur trois en variante « ne peut pas »)',
            negatif: 'un nombre négatif (−0,2 ; −1/2 ; −20 %) : les relatifs de 4e pris pour des probabilités (hypothèse)',
            valide_borne: '(variante « ne peut pas ») 0, 1, 0 % ou 100 % proposés comme valides : refuser une borne est le piège bornes_exclues',
            valide: '(variante « ne peut pas ») un nombre ordinaire de [0 ; 1]',
        },
        garantie: 'une seule option répond à la question (chaque option relue en nombre, throw sinon) ; quatre valeurs distinctes (0,5 et 1/2 ne cohabitent pas) ; la clé partage sa forme avec une autre option et n\'est pas la plus longue',
    },
    bornes_qcm_unique: {
        support: '« La probabilité d\'un événement est x. Cet événement est-il impossible, certain, ou ni l\'un ni l\'autre ? » ; trois réponses fixes ; nature visée tirée d\'abord, un tiers chacune',
        pieges: {
            certain_pour_tres_probable: '0,98 ; 99 % ; 99/100 dits certains (Eduscol 4e 2025 p. 19, DEPP 4e 2023 q.8 ; mêmes noms qu\'en 166)',
            impossible_pour_rare: '0,02 ; 1 % ; 1/100 dits impossibles',
            bornes_exclues: '0, 0 %, 0/5 (impossible) ou 1, 100 %, 5/5 (certain) dits « ni l\'un ni l\'autre » (cours p. 2, remarques)',
        },
        garantie: 'nature relue sur la valeur du nombre (throw sinon) ; 31 nombres distincts',
    },
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    nombre_qcm_unique: genNombreQcm,
    bornes_qcm_unique: genBornesQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', ordonner: 'Ordonner', classer: 'Classer', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'nombre', label: L_NOMBRE, formatsList: buildFormats('nombre') },
    { id: 'bornes', label: L_BORNES, formatsList: buildFormats('bornes') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

/** Viviers exposés à l'auto-test. */
export const _interne = { VALIDES, SUP_UN, NEGATIFS, IMPOSSIBLES, CERTAINS, TRES_PROBABLES, RARES, ORDINAIRES };
